import * as Sentry from '@sentry/browser'

/** Which Web Storage API the browser refused. */
export type StorageApi = 'localStorage' | 'sessionStorage'

/**
 * How far we got before the browser refused.
 *
 *   property  — reading `window.localStorage` itself threw. This is the
 *     STRUXHUBDESKTOP-60E shape: `SecurityError: Failed to read the
 *     'localStorage' property from 'Window'` (`DOMException.code` 18).
 *     The storage object is unreachable, so nothing about it can be probed.
 *   operation — the storage object exists but `getItem`/`setItem`/
 *     `removeItem` threw. Distinguishes a working-but-restricted store
 *     (quota, partitioned, read-only) from a wholesale denial.
 */
export type StorageDenialStage = 'property' | 'operation'

/**
 * Result of the one-shot write probe run when a denial is first seen. A
 * read-only denial and a total denial are different browser states, and
 * telling them apart from the outside is the point of the probe.
 */
export type StorageWriteProbe = 'ok' | 'failed'

/**
 * What the accessor knows at the point of failure. Deliberately a context
 * object rather than a bare message string, so a new cause-narrowing
 * dimension is one added field rather than a redesign — which is exactly
 * how `cookiesEnabled` was added below.
 *
 * On what does NOT help here, so nobody spends the time twice:
 * `navigator.storage` is quota-managed-API only and Web Storage is not
 * quota-managed, so it cannot see this. `document.hasStorageAccess()` and
 * `navigator.permissions.query({name: 'storage-access'})` answer the
 * *third-party/partitioned* question — the right instruments for an
 * embedded context, not for a top-level document, which is where this
 * fires. `navigator.cookieEnabled` is the one that actually discriminates.
 */
export interface IStorageDenial {
  api: StorageApi
  stage: StorageDenialStage
  /** `error.name`, e.g. `SecurityError`. */
  errorName?: string
  /** `DOMException.code`, e.g. 18 for SecurityError. */
  domExceptionCode?: number
  /**
   * `error.message`, verbatim. Load-bearing: all three Blink denial paths
   * throw `SecurityError` with code 18, and only the message separates them —
   * "Access is denied for this document." (cookies/site-data content setting),
   * "The document is sandboxed and lacks the 'allow-same-origin' flag.", and
   * "Storage is disabled inside 'data:' URLs." Without it the three are
   * indistinguishable in the payload.
   */
  errorMessage?: string
}

/**
 * Fixed so every denial groups into ONE Sentry issue. The varying
 * dimensions (which API, which stage, write probe, tenant) ride as tags,
 * so the issue stays queryable without fragmenting into one issue per
 * combination.
 */
const FINGERPRINT = 'web-storage-denied'
const MESSAGE = 'Web storage unavailable; continuing without persistence'

const PROBE_KEY = '__stx_storage_probe__'
const COOKIE_PROBE_KEY = '__stx_cookie_probe__'

/**
 * Reported APIs, not reported *accesses*. Storage is read on nearly every
 * interaction, so per-access reporting would be a firehose; the module
 * lives for the page session, making this once-per-session-per-API.
 */
const reportedApis = new Set<StorageApi>()

function probeWrite(api: StorageApi): StorageWriteProbe {
  try {
    const storage = globalThis[api] as Storage
    storage.setItem(PROBE_KEY, '1')
    storage.removeItem(PROBE_KEY)
    return 'ok'
  } catch {
    return 'failed'
  }
}

/**
 * Whether a cookie can actually be set. `navigator.cookieEnabled` only goes
 * false for an all-origins block (`DefaultCookiesSetting=2`); a per-site block
 * (`CookiesBlockedForUrls`, an extension, or a manual per-site Block) leaves it
 * `true` while still denying storage. This probe is what separates those two.
 */
function probeCookieWrite(): StorageWriteProbe | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }

  try {
    document.cookie = `${COOKIE_PROBE_KEY}=1;SameSite=Lax;path=/`
    const persisted = document.cookie.includes(COOKIE_PROBE_KEY)
    document.cookie = `${COOKIE_PROBE_KEY}=;Max-Age=0;SameSite=Lax;path=/`
    return persisted ? 'ok' : 'failed'
  } catch {
    return 'failed'
  }
}

/**
 * Top-level or framed. The Sentry `url` tag is the *document's* URL, so a
 * framed app looks identical to a top-level one in the payload — yet the
 * distinction decides which causes are even possible. Framed brings
 * third-party-cookie blocking, tracking prevention and storage partitioning
 * back into play; top-level rules all three out.
 */
function isTopLevel(): boolean | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    return window.top === window.self
  } catch {
    // A cross-origin parent can make even this throw, which itself means framed.
    return false
  }
}

export function describeStorageError(error: unknown): Partial<IStorageDenial> {
  return {
    errorName: error instanceof Error ? error.name : typeof error,
    errorMessage: error instanceof Error ? error.message : String(error),
    domExceptionCode:
      typeof DOMException !== 'undefined' && error instanceof DOMException
        ? error.code
        : undefined,
  }
}

/**
 * Record that storage was denied, once per session per API.
 *
 * Reported at `warning`, not `error`: the app now degrades rather than
 * dying, so this must not page anyone or spend the error budget — but it
 * must stay visible and countable. Trading a visible crash for an
 * invisible degradation would lose the only signal we have for whether
 * this is spreading beyond one customer.
 *
 * Browser name/version and OS are not set here: Sentry derives them from
 * the User-Agent and they already surface as the `browser` / `os` tags on
 * the issue.
 */
export function reportStorageDenied(denial: IStorageDenial) {
  if (reportedApis.has(denial.api)) {
    return
  }
  reportedApis.add(denial.api)

  const detail = {
    ...denial,
    writeProbe: probeWrite(denial.api),
    tenantHost: globalThis.location?.hostname,
    // The one signal that narrows the cause on a TOP-LEVEL document. Per the
    // spec, a same-origin https document only gets SecurityError when a policy
    // decision forbids persisting data, and browsers derive that from the
    // cookie setting. So `false` here means cookies/site data are blocked for
    // this site (enterprise policy or user setting) and fully explains the
    // denial; `true` means something rarer is going on and is worth a look.
    // Third-party-cookie blocking does NOT deny top-level storage, and
    // InPrivate does not either, so neither is a candidate when this is true.
    cookiesEnabled: globalThis.navigator?.cookieEnabled,
    cookieWriteProbe: probeCookieWrite(),
    topLevel: isTopLevel(),
  }

  // Always log, independent of Sentry: dev builds do not initialise Sentry
  // at all, and this is the only trace of the degraded state there.
  console.warn(MESSAGE, detail)

  Sentry.withScope(scope => {
    scope.setLevel('warning')
    scope.setFingerprint([FINGERPRINT])
    scope.setTag('storage_api', detail.api)
    scope.setTag('storage_denial_stage', detail.stage)
    scope.setTag('storage_write_probe', detail.writeProbe)
    scope.setTag('tenant_host', detail.tenantHost)
    scope.setTag('cookies_enabled', String(detail.cookiesEnabled))
    scope.setTag('cookie_write_probe', String(detail.cookieWriteProbe))
    scope.setTag('top_level', String(detail.topLevel))
    scope.setContext('web_storage_denial', detail)
    Sentry.captureMessage(MESSAGE, 'warning')
  })
}
